import {getUrlParamsFromViewSettings, RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {ofType, StateObservable} from "redux-observable";
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import {eventNotificationShow} from "../common/EventNotificationActions";
import { Observable, of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { mergeMap, map, catchError, filter, switchMap } from 'rxjs/operators';
import {
    VIEW_SORT_CHANGE, ViewActionTypes, HANDLE_SEARCH_TRIGGERED, FETCH_USER_VIEW_SETTINGS_SUCCESS, sendUserToPage
} from "../common/UserViewSettingsActions";
import { ApiReturnType } from "../../models/common/ApiReturnType";
import {UserGroup, UserGroupModule} from "../../models/user-groups/UserGroup";
import { AppState } from "../../reducers/RootReducer";
import {getInitialViewSettings} from "../../models/common/ViewSettings";
import { UserGroupsFormState } from "../../components/user-groups/UserGroupsForm";
import {ErrorAction} from "../../models/common/ActionTypes";

// ─── Fetch User Groups (List) ────────────────────────────────────────────────

/** Redux action type constant for initiating a user-groups fetch. */
export const FETCH_USER_GROUPS = "FETCH_USER_GROUPS";

/** Redux action type constant for a successful user-groups fetch. */
export const FETCH_USER_GROUPS_SUCCESS = "FETCH_USER_GROUPS_SUCCESS";

/** Redux action type constant for a failed user-groups fetch. */
export const FETCH_USER_GROUPS_ERROR = "FETCH_USER_GROUPS_ERROR";

/**
 * Creates an action to fetch the list of user groups.
 * @param viewIdentifier - Unique identifier of the view requesting the data.
 * @returns A {@link FetchUserGroupsAction} dispatched to trigger the fetch epic.
 */
export const fetchUserGroups = ( viewIdentifier: string ): FetchUserGroupsAction => ({ type: FETCH_USER_GROUPS, viewIdentifier });

/**
 * Creates a success action after user groups have been fetched.
 * @param viewIdentifier - Unique identifier of the requesting view.
 * @param payload - The API response containing an array of {@link UserGroup} items.
 * @returns A {@link FetchUserGroupsActionSuccess} with the fetched data.
 */
export const fetchUserGroupsSuccess =  ( viewIdentifier: string, payload: ApiReturnType< UserGroup[] > ): FetchUserGroupsActionSuccess => ({ type: FETCH_USER_GROUPS_SUCCESS, viewIdentifier, payload });

/**
 * Creates an error action when the user-groups fetch fails.
 * @param viewIdentifier - Unique identifier of the requesting view.
 * @param error - The error returned by the API call.
 * @returns A {@link FetchUserGroupsActionError} containing the error details.
 */
export const fetchUserGroupsError = ( viewIdentifier: string, error: any ): FetchUserGroupsActionError => ({ type: FETCH_USER_GROUPS_ERROR, viewIdentifier, error });

/** Action dispatched to initiate a user-groups fetch. */
interface FetchUserGroupsAction {
    /** Discriminant action type. */
    type: typeof FETCH_USER_GROUPS
    /** Unique identifier of the view that triggered the fetch. */
    viewIdentifier: string
}

/** Action dispatched when user groups have been fetched successfully. */
interface FetchUserGroupsActionSuccess {
    /** Discriminant action type. */
    type: typeof FETCH_USER_GROUPS_SUCCESS
    /** Paginated API response containing the user-group list. */
    payload: ApiReturnType< UserGroup[] >
    /** Unique identifier of the view that triggered the fetch. */
    viewIdentifier: string
}

/** Action dispatched when the user-groups fetch fails. */
interface FetchUserGroupsActionError {
    /** Discriminant action type. */
    type: typeof FETCH_USER_GROUPS_ERROR
    /** Unique identifier of the view that triggered the fetch. */
    viewIdentifier: string
    /** The error returned by the API call. */
    error: any
}

/** Union of all fetch-user-groups action shapes. */
export type UserGroupActionTypes = FetchUserGroupsAction | FetchUserGroupsActionSuccess | FetchUserGroupsActionError

/** Union combining user-group actions with shared view-settings actions. */
type UserGroupViewActionTypes = UserGroupActionTypes | ViewActionTypes 



/**
 * Redux-Observable epic that fetches the user-groups list from the backend.
 *
 * Reacts to sort changes, explicit fetch requests, search triggers, and
 * successful view-settings loads — but only when the active view matches
 * the user-groups route. The current view settings (pagination, sorting,
 * search) are read from the store and forwarded as URL parameters.
 *
 * @param action$ - Stream of {@link UserGroupViewActionTypes} actions.
 * @param store$ - Observable of the current {@link AppState} used to read view settings.
 * @returns An observable emitting {@link FetchUserGroupsActionSuccess} on success
 *          or {@link FetchUserGroupsActionError} together with an error notification on failure.
 */
export const fetchUserGroupsEpic = (
    action$: Observable< UserGroupViewActionTypes >,
    store$: StateObservable<AppState>
) =>
    action$.pipe(
        ofType( VIEW_SORT_CHANGE, FETCH_USER_GROUPS, HANDLE_SEARCH_TRIGGERED, FETCH_USER_VIEW_SETTINGS_SUCCESS ),
        filter( action => action.viewIdentifier === RouteConfig.userGroups.viewIdentifier ),
        mergeMap( action => {
            const userViewSettings = ( store$.value.ViewSettingsReducer.userViewSettings !== null && RouteConfig.userGroups.viewIdentifier in store$.value.ViewSettingsReducer.userViewSettings ) ? store$.value.ViewSettingsReducer.userViewSettings[ RouteConfig.userGroups.viewIdentifier ] : getInitialViewSettings( RouteConfig.userGroups.viewIdentifier );
            const path = `${ APP_CONFIG.baseWeb }system/user_groups${  getUrlParamsFromViewSettings( userViewSettings) }`;

            return ajax( APP_CONFIG.getRequestObject( path ) ).pipe(
                map( response => fetchUserGroupsSuccess(
                    action.viewIdentifier,
                    response.response as ApiReturnType< UserGroup[] >
                ) ),
                catchError( error =>
                    of(
                        fetchUserGroupsError( action.viewIdentifier, error ),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            "Nutzergruppen konnten nicht geladen werden!"
                        )
                    )
                )
            );
        })
    );

// ─── Save User Group ─────────────────────────────────────────────────────────

/** Action dispatched to persist a user group (create or update). */
export interface SaveUserGroupAction {
    /** Discriminant action type. */
    type: typeof SAVE_USER_GROUP_FORM
    /** Current state of the user-group form including edit-mode flag. */
    formState: UserGroupsFormState
}

/** Action dispatched when a user group has been saved successfully. */
export interface SaveUserGroupSuccessAction {
    /** Discriminant action type. */
    type: typeof SAVE_USER_GROUP_FORM_SUCCESS
    /** The persisted {@link UserGroup} returned by the API. */
    payload: UserGroup
}

/** Action dispatched when saving a user group fails. */
export interface SaveUserGroupErrorAction {
    /** Discriminant action type. */
    type: typeof SAVE_USER_GROUP_FORM_ERROR
    /** The error returned by the API call. */
    error: Error
}

/** Union of all save-user-group action shapes. */
export type SaveUserGroupActionTypes = SaveUserGroupAction | SaveUserGroupSuccessAction | SaveUserGroupErrorAction

/** Redux action type constant for initiating a user-group save. */
export const SAVE_USER_GROUP_FORM = "SAVE_USER_GROUP_FORM";

/** Redux action type constant for a successful user-group save. */
export const SAVE_USER_GROUP_FORM_SUCCESS = "SAVE_USER_GROUP_FORM_SUCCESS";

/** Redux action type constant for a failed user-group save. */
export const SAVE_USER_GROUP_FORM_ERROR = "SAVE_USER_GROUP_FORM_ERROR";

/**
 * Creates an action to save (create or update) a user group.
 * @param formState - The current form state containing the group data and edit-mode flag.
 * @returns A {@link SaveUserGroupAction} dispatched to trigger the save epic.
 */
export const saveUserGroupForm = ( formState: UserGroupsFormState ): SaveUserGroupAction => ({ type: SAVE_USER_GROUP_FORM, formState });

/**
 * Creates a success action after a user group has been saved.
 * @param payload - The persisted {@link UserGroup} returned by the API.
 * @returns A {@link SaveUserGroupSuccessAction} with the saved entity.
 */
export const saveUserGroupFormSuccess = ( payload: UserGroup ): SaveUserGroupSuccessAction => ({ type: SAVE_USER_GROUP_FORM_SUCCESS, payload });

/**
 * Creates an error action when saving a user group fails.
 * @param error - The error returned by the API call.
 * @returns A {@link SaveUserGroupErrorAction} containing the error details.
 */
export const saveUserGroupFormError = ( error: Error ): SaveUserGroupErrorAction => ({ type: SAVE_USER_GROUP_FORM_ERROR, error }); 

/**
 * Redux-Observable epic that persists a user group to the backend.
 *
 * Determines the HTTP method (PUT for edits, POST for new entries) based on
 * {@link UserGroupsFormState.isEditMode}. On success it refreshes the
 * user-groups list and navigates the user back to the main view.
 *
 * @param action$ - Stream of {@link SaveUserGroupAction} actions.
 * @returns An observable emitting success/error actions and an event notification.
 */
export const saveUserGroupFormEpic = ( action$: Observable< SaveUserGroupAction > ) =>
    action$.pipe(
        ofType( SAVE_USER_GROUP_FORM ),
        switchMap( action => {
            const path = ( action.formState.isEditMode ) ? `${APP_CONFIG.baseWeb}system/user_groups/${ action.formState.group.id }` : `${APP_CONFIG.baseWeb}system/user_groups`;
            const method: string = ( action.formState.isEditMode ) ? RouteMethods.put : RouteMethods.post;
            //console.log( path );
            return ajax( APP_CONFIG.getRequestObject( path, method,
                {
                    id: action.formState.group.id,
                    name: action.formState.group.name,
                    areaId: action.formState.group.areaId,
                    permissions: action.formState.group.permissions,
                    isDataBased: action.formState.group.isDataBased,
                    employeeIds: Object.values( action.formState.group.employees ).map( option => option.value )
                }) )
                    .pipe(
                        switchMap( response => of(
                                saveUserGroupFormSuccess( response.response as UserGroup ),
                                eventNotificationShow(
                                    EVENT_NOTIFICATION_TYPE.ajaxSuccess
                                ),
                                fetchUserGroups( UserGroupModule.identifier ),
                                sendUserToPage(
                                    RouteConfig.userGroups.routes.main.path,
                                    UserGroupModule.identifier
                                )
                            ) 
                        ),
                        catchError( error =>
                            of(
                                saveUserGroupFormError( error ),
                                eventNotificationShow(
                                    EVENT_NOTIFICATION_TYPE.customAjaxError,
                                    "Konnte nicht gespeichert werden!"
                                )
                            )
                        )
                    )
        } )
    );

// ─── Delete User Group ───────────────────────────────────────────────────────

/** Action dispatched to soft-delete a user group. */
export interface DeleteUserGroupAction {
    /** Discriminant action type. */
    type: typeof DELETE_USER_GROUP
    /** ID of the user group to delete. */
    userGroupId: number
}

/** Action dispatched when a user group has been deleted successfully. */
export interface DeleteUserGroupSuccessAction {
    /** Discriminant action type. */
    type: typeof DELETE_USER_GROUP_SUCCESS
    /** ID of the deleted user group. */
    userGroupId: number
}

/** Action dispatched when deleting a user group fails. */
export interface DeleteUserGroupErrorAction {
    /** Discriminant action type. */
    type: typeof DELETE_USER_GROUP_ERROR
    /** The error returned by the API call. */
    error: Error
}

/** Union of all delete-user-group action shapes. */
export type DeleteUserGroupActionTypes = DeleteUserGroupAction | DeleteUserGroupErrorAction | DeleteUserGroupSuccessAction

/** Redux action type constant for initiating a user-group deletion. */
export const DELETE_USER_GROUP = "DELETE_USER_GROUP";

/** Redux action type constant for a successful user-group deletion. */
export const DELETE_USER_GROUP_SUCCESS = "DELETE_USER_GROUP_SUCCESS";

/** Redux action type constant for a failed user-group deletion. */
export const DELETE_USER_GROUP_ERROR = "DELETE_USER_GROUP_ERROR";

/**
 * Creates an action to soft-delete a user group.
 * @param userGroupId - ID of the user group to delete.
 * @returns A {@link DeleteUserGroupAction} dispatched to trigger the delete epic.
 */
export const deleteUserGroup = ( userGroupId: number ): DeleteUserGroupAction => ({ type: DELETE_USER_GROUP, userGroupId });

/**
 * Creates a success action after a user group has been deleted.
 * @param userGroupId - ID of the deleted user group.
 * @returns A {@link DeleteUserGroupSuccessAction} confirming the deletion.
 */
export const deleteUserGroupSuccess = ( userGroupId: number ): DeleteUserGroupSuccessAction => ({ type: DELETE_USER_GROUP_SUCCESS, userGroupId });

/**
 * Creates an error action when deleting a user group fails.
 * @param error - The error returned by the API call.
 * @returns A {@link DeleteUserGroupErrorAction} containing the error details.
 */
export const deleteUserGroupError = ( error: Error ): DeleteUserGroupErrorAction => ({ type: DELETE_USER_GROUP_ERROR, error });

/**
 * Redux-Observable epic that soft-deletes a user group via the backend.
 *
 * The group is not physically removed but hidden (soft delete). On success
 * the user-groups list is refreshed and the user is navigated back to the
 * main view.
 *
 * @param action$ - Stream of {@link DeleteUserGroupAction} actions.
 * @returns An observable emitting success/error actions and an event notification.
 */
export const deleteUserGroupEpic = ( action$: Observable< DeleteUserGroupAction > ) =>
    action$.pipe(
        ofType( DELETE_USER_GROUP ),
        switchMap( action => {
           //console.log( action );
            const path = `${APP_CONFIG.baseWeb}system/user_groups/${ action.userGroupId }`;
            return ajax( APP_CONFIG.getRequestObject( path, RouteMethods.delete ) )
                    .pipe(
                        mergeMap( response => of(
                                deleteUserGroupSuccess( response.response as number ),
                                eventNotificationShow(
                                    EVENT_NOTIFICATION_TYPE.ajaxSuccess
                                ),
                                fetchUserGroups( UserGroupModule.identifier ),
                                sendUserToPage(
                                    RouteConfig.userGroups.routes.main.path,
                                    UserGroupModule.identifier
                                )
                            ) 
                        ),
                        catchError( error =>
                            of(
                                deleteUserGroupError( error ),
                                eventNotificationShow(
                                    EVENT_NOTIFICATION_TYPE.customAjaxError,
                                    "Nutzergruppe konnte nicht gelöscht werden!"
                                )
                            )
                        )
                    )
        } )
    );

// ─── Fetch User Groups By User ───────────────────────────────────────────────

/** Action dispatched to fetch all user groups assigned to a specific user. */
export interface FetchUserGroupByUserAction {
    /** Discriminant action type. */
    type: typeof FETCH_USER_GROUPS_BY_USER
    /** ID of the user whose groups should be fetched. */
    userId: number
}

/** Action dispatched when user groups for a specific user have been fetched successfully. */
export interface FetchUserGroupByUserSuccessAction {
    /** Discriminant action type. */
    type: typeof FETCH_USER_GROUPS_BY_USER_SUCCESS
    /** ID of the user the groups belong to. */
    userId: number
    /** Array of {@link UserGroup} items assigned to the user. */
    groups: UserGroup[]
}

/** Action dispatched when fetching user groups for a specific user fails. */
export interface FetchUserGroupByUserErrorAction {
    /** Discriminant action type. */
    type: typeof FETCH_USER_GROUPS_BY_USER_ERROR
    /** The error returned by the API call. */
    error: Error
}

/** Union of all fetch-user-groups-by-user action shapes. */
export type FetchUserGroupByUserActionTypes = FetchUserGroupByUserAction | FetchUserGroupByUserSuccessAction | FetchUserGroupByUserErrorAction

/** Redux action type constant for initiating a fetch of user groups by user. */
export const FETCH_USER_GROUPS_BY_USER = "FETCH_USER_GROUPS_BY_USER";

/** Redux action type constant for a successful fetch of user groups by user. */
export const FETCH_USER_GROUPS_BY_USER_SUCCESS = "FETCH_USER_GROUPS_BY_USER_SUCCESS";

/** Redux action type constant for a failed fetch of user groups by user. */
export const FETCH_USER_GROUPS_BY_USER_ERROR = "FETCH_USER_GROUPS_BY_USER_ERROR";

/**
 * Creates an action to fetch all user groups assigned to a given user.
 * @param userId - ID of the user whose groups should be loaded.
 * @returns A {@link FetchUserGroupByUserAction} dispatched to trigger the epic.
 */
export const fetchUserGroupsByUser = ( userId: number ): FetchUserGroupByUserAction => ({ type: FETCH_USER_GROUPS_BY_USER, userId });

/**
 * Creates a success action after user groups for a specific user have been fetched.
 * @param userId - ID of the user the groups belong to.
 * @param groups - Array of {@link UserGroup} items returned by the API.
 * @returns A {@link FetchUserGroupByUserSuccessAction} with the fetched data.
 */
export const fetchUserGroupsByUserSuccess = ( userId: number, groups: UserGroup[] ): FetchUserGroupByUserSuccessAction =>
    ({ type: FETCH_USER_GROUPS_BY_USER_SUCCESS, userId, groups });

/**
 * Creates an error action when fetching user groups for a specific user fails.
 * @param error - The error returned by the API call.
 * @returns A {@link FetchUserGroupByUserErrorAction} containing the error details.
 */
export const fetchUserGroupsByUserError = ( error: Error ): FetchUserGroupByUserErrorAction =>
    ({ type:FETCH_USER_GROUPS_BY_USER_ERROR, error });

/**
 * Redux-Observable epic that fetches all user groups assigned to a specific user.
 *
 * Calls the `system/user_groups/users/:userId` endpoint and maps the response
 * to a success action or emits an error notification on failure.
 *
 * @param action$ - Stream of {@link FetchUserGroupByUserAction} actions.
 * @returns An observable emitting {@link FetchUserGroupByUserSuccessAction} on success
 *          or {@link FetchUserGroupByUserErrorAction} together with an error notification on failure.
 */
export const fetchUserGroupsByUserEpic = ( action$: Observable< FetchUserGroupByUserAction > ) =>
    action$.pipe(
        ofType( FETCH_USER_GROUPS_BY_USER ),
        mergeMap( action => {
            const path = `${APP_CONFIG.baseWeb}system/user_groups/users/${ action.userId }`;

            return ajax( APP_CONFIG.getRequestObject( path ) )
                .pipe(
                    map( response => fetchUserGroupsByUserSuccess( action.userId, response.response as UserGroup[] ) ),
                    catchError( error => of(
                        fetchUserGroupsByUserError( error ),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            "Nutzergruppen des Nutzers konnten nicht geladen werden"
                        )
                    ))
                )
        })
    );

// ─── Fetch Single User Group ─────────────────────────────────────────────────

/** Action dispatched to fetch a single user group by its ID. */
export interface FetchUserGroupAction {
    /** Discriminant action type. */
    type: typeof FETCH_USER_GROUP
    /** ID of the user group to fetch. */
    groupId: number
}

/** Action dispatched when a single user group has been fetched successfully. */
export interface FetchUserGroupSuccessAction {
    /** Discriminant action type. */
    type: typeof FETCH_USER_GROUP_SUCCESS
    /** The fetched {@link UserGroup} entity. */
    group: UserGroup
}

/** Action dispatched when fetching a single user group fails. Extends the shared {@link ErrorAction} shape. */
export interface FetchUserGroupErrorAction extends ErrorAction< typeof FETCH_USER_GROUP_ERROR> {}

/** Union of all fetch-single-user-group action shapes. */
export type FetchUserGroupActionTypes = FetchUserGroupAction | FetchUserGroupSuccessAction | FetchUserGroupErrorAction

/** Redux action type constant for initiating a single user-group fetch. */
export const FETCH_USER_GROUP = "FETCH_USER_GROUP";

/** Redux action type constant for a successful single user-group fetch. */
export const FETCH_USER_GROUP_SUCCESS = "FETCH_USER_GROUP_SUCCESS";

/** Redux action type constant for a failed single user-group fetch. */
export const FETCH_USER_GROUP_ERROR = "FETCH_USER_GROUP_ERROR";

/**
 * Creates an action to fetch a single user group.
 * @param groupId - ID of the user group to load.
 * @returns A {@link FetchUserGroupAction} dispatched to trigger the fetch epic.
 */
export const fetchUserGroup = ( groupId: number ): FetchUserGroupAction => ({ type: FETCH_USER_GROUP, groupId });

/**
 * Creates a success action after a single user group has been fetched.
 * @param group - The {@link UserGroup} entity returned by the API.
 * @returns A {@link FetchUserGroupSuccessAction} with the fetched entity.
 */
export const fetchUserGroupSuccess = ( group: UserGroup ): FetchUserGroupSuccessAction => ({ type: FETCH_USER_GROUP_SUCCESS, group });

/**
 * Creates an error action when fetching a single user group fails.
 * @param error - The error returned by the API call.
 * @returns A {@link FetchUserGroupErrorAction} containing the error details.
 */
export const fetchUserGroupError = ( error: Error ): FetchUserGroupErrorAction => ({ type: FETCH_USER_GROUP_ERROR, error });

/**
 * Redux-Observable epic that fetches a single user group from the backend.
 *
 * Calls the `system/user_groups/:groupId` endpoint and maps the response
 * to a success action or emits an error notification on failure.
 *
 * @param action$ - Stream of {@link FetchUserGroupAction} actions.
 * @param store$ - Observable of the current {@link AppState} (available for future use).
 * @returns An observable emitting {@link FetchUserGroupSuccessAction} on success
 *          or {@link FetchUserGroupErrorAction} together with an error notification on failure.
 */
export const fetchUserGroupEpic = ( action$: Observable< FetchUserGroupAction >, store$: StateObservable<AppState> ) =>
    action$.pipe(
        ofType( FETCH_USER_GROUP ),
        mergeMap( action => {
            const path = `${ APP_CONFIG.baseWeb }system/user_groups/${  action.groupId }`;

            return ajax( APP_CONFIG.getRequestObject( path ) ).pipe(
                map( response => fetchUserGroupSuccess( response.response as UserGroup ) ),
                catchError( error =>
                    of(
                        fetchUserGroupError( error ),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            "Nutzergruppen konnten nicht geladen werden!"
                        )
                    )
                )
            );
        })
    );
